import { useCallback, useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Alert, Flex, IconPrinterOutline, TextButton } from '@zillow/constellation';

import BackLink from './BackLink';
import { useHistory } from 'react-router-dom';
import { getApplicantPath, getIdVerificationPath, URLS } from '../../../../../constants/urls';
import useQueryVariables from '../../../../../hooks/useQueryVariables';
import { idStatusTypes, termsStatusTypes } from '../../../../../constants/statusConstants';
import styled from 'styled-components';
import LoadingScreen from '../../../../../components/LoadingScreen/LoadingScreen';
import { PAGE_DATA_ERROR } from '../../../../../constants/stringConstants';
import Summary from './Summary';
import EmploymentHistory from './EmploymentHistory';
import Income from './Income';
import ResidenceHistory from './ResidenceHistory';
import { useGetIsIdvModuleEnabled } from '../../../../../hooks/useGetSplitAb';
import { emitPrintApplicationEvent } from '../../../../../../client/analytics/clickstreamEvents';
import { useListingUser } from '../../../../../hooks/useListingUser';

const QUERY = gql`
  query ApplicantDetails {
    user {
      idVerification {
        status
      }
      termSet(id: "landlordScreening") {
        status
      }
    }
  }
`;

const StyledTextButton = styled(TextButton)`
  @media print {
    display: none;
  }
`;

export default function ApplicantDetails() {
  const { user, listing } = useListingUser();
  const history = useHistory();
  const { propertyId, applicationId, zgAuthZone, applicantId } = useQueryVariables();
  const query = new URLSearchParams({
    propertyId,
    applicationId,
    zgAuthZone,
    applicantId,
  });

  const isIdvModuleEnabled = useGetIsIdvModuleEnabled();

  const applicantSummaryLink = `/rental-manager/applications/pdfs/applicant-summary.pdf?${query}`;

  const onPrint = () => {
    setTimeout(() => {
      emitPrintApplicationEvent(listing, user);
      window.open(applicantSummaryLink, '_blank').print();
    }, 100);
  };

  const onCompleted = useCallback(
    (completedData) => {
      const {
        user: {
          idVerification: { status: idVerificationStatus },
          termSet: { status: termSetStatus },
        },
      } = completedData;
      const hasCurrentTerms = termSetStatus === termsStatusTypes.TAC_CURRENTLY_ACCEPTED;
      const isCurrentlyIdentified = idVerificationStatus === idStatusTypes.CURRENTLY_IDENTIFIED;
      if (!hasCurrentTerms || !isCurrentlyIdentified) {
        const idVerificationPath = isIdvModuleEnabled
          ? getIdVerificationPath(propertyId)
          : getApplicantPath({
              propertyId,
              applicationId,
              pageLocation: `${URLS.SCREENING}/${URLS.SCREENING_LANDLORD_JUNCTION}`,
              zgAuthZone,
              applicantId,
            });
        history.push(idVerificationPath);
      }
    },
    [history, applicantId, applicationId, zgAuthZone, propertyId, isIdvModuleEnabled],
  );
  const { loading, error } = useQuery(QUERY, {
    fetchPolicy: 'cache-and-network',
    onCompleted,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return <LoadingScreen className="rental-application__loading-screen--transparent-bg" show isLoading />;
  }
  if (error) {
    return <Alert appearance="warning">{PAGE_DATA_ERROR}</Alert>;
  }

  return (
    <Flex display="flex" flexDirection="column" id="applicant-details-refresh">
      <Flex display="flex" alignItems="center" justifyContent="space-between">
        <BackLink />
        <StyledTextButton icon={<IconPrinterOutline />} fontType="bodySmallHeading" padding="xs" onClick={onPrint}>
          Print
        </StyledTextButton>
      </Flex>
      <Summary />
      <Income />
      <EmploymentHistory />
      <ResidenceHistory />
    </Flex>
  );
}
